/**
 * TenantBlockedDialog
 * Global modal shown when a write is refused because the workspace is
 * suspended or inactive (read-only). Sibling to PermissionLockDialog.tsx
 * (mounted once in AdminLayout, state in tenantBlockedSlice.ts) but with
 * wording per state — the dialog explains WHY nothing can be saved, since the
 * rest of the app still renders normally in read-only mode.
 */

import React from 'react';
import { Dialog, DialogContent, Box, Typography, Button } from '@mui/material';
import { PauseCircleOutline } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { REACTIVATE_ROUTE } from '@/utils/tenant-block-detection.util';
import { RootState, AppDispatch } from '@/store';
import { hideTenantBlocked } from '@/store/slices/tenantBlockedSlice';

const COPY = {
  suspended: {
    title: 'Workspace suspended',
    hint: 'Your data is safe and still viewable. Please contact support to get this workspace restored.',
  },
  inactive: {
    title: 'Workspace inactive',
    hint: 'Your data is safe and still viewable. Changes, messages and automations are paused until the workspace is reactivated — you can do that by choosing a paid plan.',
  },
} as const;

const TenantBlockedDialog: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { open, payload } = useSelector((state: RootState) => state.tenantBlocked);

  const handleClose = () => dispatch(hideTenantBlocked());

  if (!payload) return null;
  const copy = COPY[payload.status];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', p: 4 }}>
        <PauseCircleOutline
          sx={{
            fontSize: 48,
            mb: 2,
            color: payload.status === 'suspended' ? 'error.main' : 'warning.main',
          }}
        />
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {copy.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {payload.message}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          {copy.hint}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Only an INACTIVE workspace can reactivate itself (by paying); suspended = contact support. */}
          {payload.status === 'inactive' && (
            <Button
              variant="contained"
              onClick={() => {
                handleClose();
                navigate(REACTIVATE_ROUTE);
              }}
            >
              Reactivate workspace
            </Button>
          )}
          <Button variant={payload.status === 'inactive' ? 'text' : 'contained'} onClick={handleClose}>
            Got it
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TenantBlockedDialog;
